import * as _ from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { accountToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import useNavigate from '../utils/router/useNavigate';
import usePostLoginHandler from './usePostLoginHandler';

function getActionForError(error: string, platform: string) {
  const displayPlatform = _.capitalize(platform);

  switch (error) {
    case 'missing-integration':
      return UIActions.pushToast(
        accountToasts.missingIntegrationError({ platform: displayPlatform }),
      );
    case 'missing-membership': {
      return UIActions.pushToast(
        accountToasts.missingMembershipError({ platform: displayPlatform }),
      );
    }
    case 'seat-limit-maxed':
      return UIActions.pushToast(accountToasts.seatLimitMaxedError);
    case 'user-creation-disallowed':
      return UIActions.pushToast(accountToasts.userCreationDisallowedError);
    case 'deactivated':
      return UIActions.pushToast(accountToasts.deactivatedError);
    default:
      return UIActions.pushToast(accountToasts.unknownLoginError);
  }
}

interface LoginHandlerProps {
  children: React.ReactNode;
}

const LoginHandler = ({ children }: LoginHandlerProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authToken = searchParams.get('authToken');
  const error = searchParams.get('error');
  const platform = searchParams.get('platform');

  const { pathname } = useLocation();
  const postLoginHandler = usePostLoginHandler();
  const [isCapturingToken, setIsCapturingToken] = useState(false);

  useEffect(() => {
    if (authToken) {
      setIsCapturingToken(true);
      postLoginHandler(authToken);
    } else if (error) {
      const action = getActionForError(error, platform ?? '');
      dispatch(action);
      navigate(pathname, { replace: true });
    }
  }, [
    navigate,
    authToken,
    error,
    platform,
    pathname,
    dispatch,
    postLoginHandler,
  ]);

  return isCapturingToken ? <div>Logging you in...</div> : <>{children}</>;
};

export default LoginHandler;
